// apps/client/src/net/handlers/core.ts — presence, spaces, residents, the town, your private patches, toasts and world objects.
// The Game runs this table first for every event, then every other net/handlers module in module order (§8.6).
import { hasString } from '@whiskerton/content';
import type { PresenceEntry, ResidentId, ResidentPublic } from '@whiskerton/shared';
import type { NetHandlers } from '../../game/game';
import { useGame } from '../../state/store';

function patchPresence(r: ResidentId, p: Partial<PresenceEntry>): void {
  const presence = useGame.getState().presence, cur = presence[r];
  const next = { ...(cur ?? { r, space: 'out', away: false }), ...p } as PresenceEntry;
  useGame.setState({ presence: { ...presence, [r]: next } });
}
function dropPresence(r: ResidentId): void {
  const { [r]: _gone, ...rest } = useGame.getState().presence;
  useGame.setState({ presence: rest as Record<ResidentId, PresenceEntry> });
}
function setResident(r: ResidentPublic): void {
  useGame.setState({ residents: { ...useGame.getState().residents, [r.id]: r } });
}
function dropResident(r: ResidentId): void {
  const { [r]: _gone, ...rest } = useGame.getState().residents;
  useGame.setState({ residents: rest as Record<ResidentId, ResidentPublic> });
}

export const handlers: NetHandlers = {
  events: {
    'presence.join': (e, g) => { patchPresence(e.r.id, { r: e.r.id, space: e.space, away: false }); setResident(e.r); g.tags?.set(e.r.id, e.r.name, e.r.color, false); },
    'presence.away': (e, g) => { patchPresence(e.r, { away: true }); g.refreshTag(e.r); },
    'presence.back': (e, g) => { patchPresence(e.r, { away: false }); g.refreshTag(e.r); },
    'presence.leave': (e, g) => { dropPresence(e.r); g.despawn(e.r); g.tags?.remove(e.r); },
    'presence.space': (e) => patchPresence(e.r, { space: e.space }),
    'space.enter': (e, g) => void g.spawn(e.r, e.s),
    'space.leave': (e, g) => g.despawn(e.r),
    'resident.joined': (e, g) => { setResident(e.r); g.syncHouseTags(); },
    'resident.updated': (e, g) => { setResident(e.r); g.restyle(e.r.id); g.syncHouseTags(); },   // a new name or colour shows on the house too
    // A removed or deleted resident's socket is closed without presence.leave (server closeResident), so they go from the scene here.
    'resident.left': (e, g) => { dropPresence(e.r); dropResident(e.r); g.despawn(e.r); g.tags?.remove(e.r); g.syncHouseTags(); },
    'town.updated': (e) => useGame.setState({ town: e.town }),
    'you.patch': (e, g) => { useGame.getState().applyPatch(e.patch); g.afterPatch(e.patch); },   // afterPatch runs every module's onPatch and refreshPerResident
    toast: (e) => useGame.getState().toast(hasString(e.key) ? e.key : 'common.notice', e.params),
    'object.added': (e, g) => { void g.scenes.out?.addObject(e.obj); g.invalidateNav(); g.syncHouseTags(); },
    'object.updated': (e, g) => { g.scenes.out?.updateObject(e.obj); g.invalidateNav(); g.syncHouseTags(); },   // e.g. a house's occupant changed
    'object.removed': (e, g) => { g.scenes.out?.removeObject(e.id); g.invalidateNav(); g.syncHouseTags(); },
  },
};
